import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Link, Typography } from '@mui/material';
import { LEGAL_EFFECTIVE_DATE, PRIVACY_POLICY, TERMS_OF_SERVICE } from '../content/legal';

const components: Components = {
  h2: ({ children }) => (
    <Typography variant="h6" component="h2" sx={{ fontWeight: 650, mt: 4, mb: 1.25 }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.75 }}>
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <Typography component="li" variant="body1" sx={{ mb: 0.75, lineHeight: 1.7 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ pl: 3, mt: 0, mb: 2 }}>
      {children}
    </Box>
  ),
  a: ({ href, children }) => (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {children}
    </Link>
  ),
  table: ({ children }) => (
    <Box sx={{ overflowX: 'auto', my: 2.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.92rem',
          '& th, & td': { px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', textAlign: 'left', verticalAlign: 'top' },
          '& th': { fontWeight: 650, bgcolor: 'action.hover' },
          '& tr:last-of-type td': { borderBottom: 0 },
        }}
      >
        {children}
      </Box>
    </Box>
  ),
};

/** Public legal page: Terms of Service or Privacy Policy. */
export const Legal: React.FC<{ doc: 'terms' | 'privacy' }> = ({ doc }) => {
  const isTerms = doc === 'terms';
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Typography variant="h3" component="h1" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '2rem', sm: '2.5rem' } }}>
        {isTerms ? 'Terms of Service' : 'Privacy Policy'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Effective {LEGAL_EFFECTIVE_DATE} ·{' '}
        <Link component={RouterLink} to={isTerms ? '/privacy' : '/terms'} underline="hover">
          {isTerms ? 'Privacy Policy' : 'Terms of Service'}
        </Link>
      </Typography>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {isTerms ? TERMS_OF_SERVICE : PRIVACY_POLICY}
      </ReactMarkdown>
    </Container>
  );
};
